import { getAllPosts } from 'lib/posts';
import { getAllShows } from 'lib/shows';
import { getAllEvents } from 'lib/events';
import { formatDate } from 'lib/datetime';
import { calculateEpisodeNumber, getFeaturedImageUrl } from 'lib/util';
import {
  C,
  FONT,
  YOUTUBE_CHANNEL_URL,
  button,
  buttonRow,
  chamfer,
  dateBadge,
  emailFooter,
  emailHeader,
  escapeHtml,
  eventCard,
  getAbsoluteUrl,
  getEventLocation,
  getItemTags,
  getTicketUrl,
  isUpcoming,
  sanitizeHtml,
  sectionHeader,
  thumb,
  truncate,
  withUtm,
} from 'lib/newsletter-email';
import EmailDocument from 'components/EmailDocument';
import { formatHostNames } from 'lib/showHosts';

const YOUTUBE_FEED_URL = 'https://www.youtube.com/feeds/videos.xml?channel_id=UC8Iw3TQW1r5dXibJf77pPbA';

export default function Newsletter({
  posts,
  upcomingShows,
  pastShows,
  upcomingEvents,
  pastEvents,
  featuredStickyEvent,
  topTags,
  youtubeVideos,
}) {
  // Use current date instead of route parameters
  const newsletterDate = new Date();
  const formattedDate = formatDate(newsletterDate.toISOString(), 'MMMM d, yyyy');
  const utmCampaign = `newsletter-${formatDate(newsletterDate.toISOString(), 'yyyy-MM')}`;
  const trackedUrl = (path, content) => withUtm(getAbsoluteUrl(path), utmCampaign, content);

  // Get the next upcoming show for the "This Week's Show" section
  const nextShow = upcomingShows?.[0];
  const stickyUpcomingEvent = upcomingEvents?.find((event) => event?.isSticky);
  const featuredEvent = stickyUpcomingEvent || featuredStickyEvent;
  const upcomingEventsList = upcomingEvents;
  const featuredTicketUrl = getTicketUrl(featuredEvent);
  const [latestVideo, ...moreVideos] = youtubeVideos || [];
  const preheader = featuredEvent
    ? `${featuredEvent.title}${
        featuredEvent.eventDate ? ` (${formatDate(featuredEvent.eventDate, 'MMM d')})` : ''
      }, plus upcoming events, new videos and this week's Frequency show.`
    : "Upcoming events, new videos and this week's Frequency show from Turnstyle.";

  // Shared card for features + show archives: thumbnail left, copy right; stacks on phones.
  const mediaCard = ({ href, image, title, meta, kicker, hosts = [], excerpt, cta, ctaContent }) => `
              <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="margin-bottom: 16px; background-color: #ffffff; border: 1px solid ${
                C.line
              }; border-left: 4px solid ${C.brand};">
                <tr>
                  <td class="nl-stack nl-thumb" width="156" valign="top" style="padding: 16px 0 16px 16px;">
                    <a href="${href}">${thumb(image, title, 140, 140)}</a>
                  </td>
                  <td class="nl-stack" valign="top" style="padding: 16px 18px;">
                    ${
                      kicker
                        ? `<p style="margin: 0 0 4px 0; font-family: ${FONT}; font-size: 11px; font-weight: 800; letter-spacing: 1.5px; text-transform: uppercase; color: ${C.brandDark};">${kicker}</p>`
                        : ''
                    }
                    <h3 style="text-transform: none; margin: 0 0 4px 0; padding: 0; font-family: ${FONT}; font-size: 18px; font-weight: 800; line-height: 1.3; color: ${
    C.ink
  };"><a href="${href}" style="color: ${C.ink}; text-decoration: none;">${escapeHtml(title)}</a></h3>
                    ${
                      meta
                        ? `<p style="margin: 0 0 8px 0; font-family: ${FONT}; font-size: 13px; font-weight: 600; color: ${C.muted};">${meta}</p>`
                        : ''
                    }
                    ${
                      hosts.length
                        ? `<p style="margin: 0 0 8px 0; font-family: ${FONT}; font-size: 13px; color: ${
                            C.body
                          };">Hosted by <strong>${escapeHtml(formatHostNames(hosts))}</strong></p>`
                        : ''
                    }
                    ${
                      excerpt
                        ? `<p style="margin: 0 0 12px 0; font-family: ${FONT}; font-size: 14px; line-height: 1.55; color: ${C.body};">${excerpt}</p>`
                        : ''
                    }
                    <a href="${ctaContent}" style="font-family: ${FONT}; font-size: 13px; font-weight: 800; color: ${
    C.brandDark
  }; text-decoration: none; letter-spacing: 0.3px;">${cta} &rarr;</a>
                  </td>
                </tr>
              </table>`;

  const ytThumb = (video, size) => `https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`;
  // Thumbnail as a cell background so the play button can sit on top without
  // positioning (Gmail strips negative margins/position); bgcolor covers Outlook.
  const videoTile = (video, size, height, badge, content) => {
    const src = ytThumb(video, size);
    return `<a href="${trackedUrl(
      video.url,
      content
    )}" style="display: block; text-decoration: none;" aria-label="${escapeHtml(
      `Watch ${video.title} on YouTube`
    )}"><table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="${chamfer(
      size === 'lg' ? 16 : 10
    )}"><tr><td background="${src}" bgcolor="#000000" height="${height}" align="center" valign="middle" style="height: ${height}px; background-color: #000000; background-image: url('${src}'); background-size: cover; background-position: center;">
      <table role="presentation" cellspacing="0" cellpadding="0" border="0"><tr><td width="${badge}" height="${Math.round(
      badge * 0.7
    )}" align="center" valign="middle" style="background-color: ${
      C.youtube
    }; color: #ffffff; font-family: ${FONT}; font-size: ${Math.round(
      badge * 0.36
    )}px; line-height: 1; border-radius: ${Math.round(badge * 0.22)}px;">&#9654;</td></tr></table>
    </td></tr></table></a>`;
  };

  return (
    <EmailDocument
      preheader={preheader}
      rows={`
          ${emailHeader({
            trackedUrl,
            dateLabel: formattedDate,
            title: 'The Turnstyle<br />Newsletter',
            subtitle: 'Upcoming events, the Frequency radio show, and new features from the crew.',
          })}

          ${
            featuredEvent
              ? `
          <!-- Featured Sticky Event -->
          <tr>
            <td class="nl-pad" style="padding: 40px 40px 36px 40px; background-color: #ffffff;">
              ${sectionHeader('Don&rsquo;t miss it', 'Featured Event')}
              <a href="${trackedUrl(`/event/${featuredEvent.slug}`, 'featured_event_image')}">
                <img src="${getFeaturedImageUrl(featuredEvent.featuredImage)}" alt="${escapeHtml(
                  featuredEvent.title || 'Event image'
                )}" width="570" style="width: 100%; height: auto; display: block; border: 0; ${chamfer(20)}" />
              </a>
              <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="margin-top: 20px;">
                <tr>
                  ${
                    featuredEvent.eventDate
                      ? `<td width="80" valign="top" style="padding-right: 16px;">${dateBadge(
                          featuredEvent.eventDate
                        )}</td>`
                      : ''
                  }
                  <td valign="top">
                    <h3 style="text-transform: none; margin: 0 0 6px 0; padding: 0; font-family: ${FONT}; font-size: 26px; font-weight: 900; line-height: 1.15; letter-spacing: -0.3px; color: ${
                  C.ink
                };">
                      <a href="${trackedUrl(`/event/${featuredEvent.slug}`, 'featured_event_title')}" style="color: ${
                  C.ink
                }; text-decoration: none;">${escapeHtml(featuredEvent.title || 'Untitled Event')}</a>
                    </h3>
                    ${
                      getEventLocation(featuredEvent)
                        ? `<p style="margin: 0; font-family: ${FONT}; font-size: 14px; font-weight: 600; color: ${
                            C.muted
                          };">${escapeHtml(getEventLocation(featuredEvent))}</p>`
                        : ''
                    }
                  </td>
                </tr>
              </table>
              ${
                featuredEvent.excerpt
                  ? `<p style="margin: 16px 0 0 0; font-family: ${FONT}; font-size: 15px; line-height: 1.6; color: ${
                      C.body
                    };">${sanitizeHtml(featuredEvent.excerpt)}</p>`
                  : ''
              }
              <div style="padding-top: 20px;">
                ${buttonRow([
                  featuredTicketUrl &&
                    button(
                      trackedUrl(featuredTicketUrl, 'featured_event_tickets'),
                      'Get Tickets &rarr;',
                      'tickets',
                      'lg'
                    ),
                  button(
                    trackedUrl(`/event/${featuredEvent.slug}`, 'featured_event_details'),
                    'Event Details',
                    featuredTicketUrl ? 'outline' : 'solid',
                    'lg'
                  ),
                ])}
              </div>
            </td>
          </tr>
          `
              : ''
          }

          ${
            upcomingEventsList && upcomingEventsList.length > 0
              ? `
          <!-- Upcoming Events -->
          <tr>
            <td class="nl-pad" style="padding: 40px 40px 36px 40px; background-color: ${C.tint};">
              ${sectionHeader('On the calendar', 'Upcoming Events')}
              ${upcomingEventsList.map((event) => eventCard({ event, trackedUrl })).join('')}
              <div style="padding-top: 12px; text-align: center;">
                ${button(trackedUrl('/events', 'view_all_events'), 'View All Events &rarr;', 'solid', 'lg')}
              </div>
            </td>
          </tr>
          `
              : ''
          }

          <!-- YouTube -->
          <tr>
            <td class="nl-pad" bgcolor="${C.night}" style="padding: 40px 40px 40px 40px; background-color: ${
        C.night
      }; background-image: linear-gradient(135deg, ${C.night} 0%, #2a0f45 60%, #3d0d2c 100%);">
              ${sectionHeader('Watch &middot; Fresh every week', 'Turnstyle on YouTube', { dark: true })}
              ${
                latestVideo
                  ? `
              ${videoTile(latestVideo, 'lg', 320, 72, 'youtube_latest_thumb')}
              <p style="margin: 16px 0 0 0; font-family: ${FONT}; font-size: 11px; font-weight: 800; letter-spacing: 1.5px; text-transform: uppercase; color: ${
                      C.brand
                    };">Latest upload${
                      latestVideo.published ? ` &middot; ${formatDate(latestVideo.published, 'MMM d')}` : ''
                    }</p>
              <h3 style="text-transform: none; margin: 4px 0 0 0; font-family: ${FONT}; font-size: 20px; font-weight: 800; line-height: 1.3; color: #ffffff;">
                <a href="${trackedUrl(
                  latestVideo.url,
                  'youtube_latest_title'
                )}" style="color: #ffffff; text-decoration: none;">${escapeHtml(latestVideo.title)}</a>
              </h3>
              ${
                moreVideos.length
                  ? `
              <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="margin-top: 20px;">
                <tr>
                  ${moreVideos
                    .slice(0, 2)
                    .map(
                      (video, i) => `
                  ${i ? '<td width="16" style="font-size: 0;">&nbsp;</td>' : ''}
                  <td width="50%" valign="top">
                    ${videoTile(video, 'sm', 150, 44, `youtube_more_${video.id}`)}
                    <p style="margin: 10px 0 0 0; font-family: ${FONT}; font-size: 14px; font-weight: 700; line-height: 1.35; color: #e9ddfa;">${escapeHtml(
                        video.title
                      )}</p>
                  </td>`
                    )
                    .join('')}
                </tr>
              </table>`
                  : ''
              }`
                  : `
              <p style="margin: 0; font-family: ${FONT}; font-size: 16px; line-height: 1.6; color: #e9ddfa;">
                Full DJ sets, Frequency radio episodes, event recaps and behind-the-scenes clips.
              </p>`
              }
              <p style="margin: 24px 0 0 0; font-family: ${FONT}; font-size: 15px; line-height: 1.55; color: #e9ddfa;">
                Subscribe so you never miss a set, a Frequency episode, or the next event announcement.
              </p>
              <div style="padding-top: 16px;">
                ${buttonRow([
                  button(
                    trackedUrl(`${YOUTUBE_CHANNEL_URL}?sub_confirmation=1`, 'youtube_callout'),
                    '&#9654;&nbsp; Subscribe on YouTube',
                    'youtube',
                    'lg'
                  ),
                ])}
              </div>
            </td>
          </tr>

          ${
            nextShow
              ? `
          <!-- This Week's Show -->
          <tr>
            <td class="nl-pad" style="padding: 40px 40px 24px 40px; background-color: ${C.tint};">
              ${sectionHeader('Frequency on WRIR 97.3FM', 'This Week&rsquo;s Show')}
              ${mediaCard({
                href: trackedUrl(`/shows/${nextShow.slug}`, 'this_weeks_show_title'),
                image: getFeaturedImageUrl(nextShow.featuredImage),
                title: nextShow.title || 'Untitled Show',
                kicker: nextShow.showDate ? calculateEpisodeNumber(nextShow.showDate || nextShow.date) : '',
                meta: nextShow.showDate ? formatDate(nextShow.showDate || nextShow.date, 'EEEE, MMMM d') : '',
                hosts: nextShow.showHosts || [],
                excerpt: nextShow.excerpt ? truncate(nextShow.excerpt, 220) : '',
                cta: 'Tune In',
                ctaContent: trackedUrl(`/shows/${nextShow.slug}`, 'this_weeks_show_learn_more'),
              })}
            </td>
          </tr>
          `
              : ''
          }

          ${
            posts && posts.length > 0
              ? `
          <!-- Features -->
          <tr>
            <td class="nl-pad" style="padding: 40px 40px 36px 40px; background-color: #ffffff;">
              ${sectionHeader('Read', 'Features')}
              ${posts
                .slice(0, 3)
                .map((post) =>
                  mediaCard({
                    href: trackedUrl(`/posts/${post.slug}`, `post_title_${post.slug}`),
                    image: getFeaturedImageUrl(post.featuredImage),
                    title: post.title || 'Untitled Post',
                    meta: post.date ? formatDate(post.date, 'MMMM d, yyyy') : '',
                    excerpt: post.excerpt ? truncate(post.excerpt) : '',
                    cta: 'Read More',
                    ctaContent: trackedUrl(`/posts/${post.slug}`, `post_read_more_${post.slug}`),
                  })
                )
                .join('')}
              <div style="padding-top: 12px; text-align: center;">
                ${button(trackedUrl('/posts', 'read_all_posts'), 'Read All Posts &rarr;', 'solid', 'lg')}
              </div>
            </td>
          </tr>
          `
              : ''
          }

          ${
            pastShows && pastShows.length > 0
              ? `
          <!-- Show Archives -->
          <tr>
            <td class="nl-pad" style="padding: 40px 40px 36px 40px; background-color: ${C.tint};">
              ${sectionHeader('Listen back', 'Show Archives')}
              ${pastShows
                .slice(0, 3)
                .map((show) =>
                  mediaCard({
                    href: trackedUrl(`/shows/${show.slug}`, `show_archive_title_${show.slug}`),
                    image: getFeaturedImageUrl(show.featuredImage),
                    title: show.title || 'Untitled Show',
                    kicker: show.showDate ? calculateEpisodeNumber(show.showDate || show.date) : '',
                    meta: show.showDate ? formatDate(show.showDate || show.date, 'MMMM d, yyyy') : '',
                    hosts: show.showHosts || [],
                    excerpt: show.excerpt ? truncate(show.excerpt) : '',
                    cta: 'Listen',
                    ctaContent: trackedUrl(`/shows/${show.slug}`, `show_archive_listen_${show.slug}`),
                  })
                )
                .join('')}
              <div style="padding-top: 12px; text-align: center;">
                ${button(trackedUrl('/shows', 'browse_all_shows'), 'Browse All Shows &rarr;', 'solid', 'lg')}
              </div>
            </td>
          </tr>
          `
              : ''
          }

          ${emailFooter({ trackedUrl })}

          ${
            // Disabled until tags are cleaned up; re-enable by removing the `false &&` below.
            false && topTags && topTags.length > 0
              ? `
          <!-- Tag Cloud -->
          <tr>
            <td style="padding: 30px 40px 40px 40px; background-color: #f9fafb; border-top: 1px solid #e5e7eb; text-align: center;">
              <h3 style="margin: 0 0 16px 0; padding: 0; font-size: 14px; font-weight: 600; color: #6b7280; font-family: ${FONT}; text-transform: uppercase; letter-spacing: 0.5px;">
                #quicklinks
              </h3>
              <p style="margin: 0; padding: 0;">
                ${topTags
                  .map(
                    (tag) => `
                <a href="${trackedUrl(
                  `/tag/${tag.slug}`,
                  `tag_${tag.slug}`
                )}" style="display: inline-block; margin: 5px 5px 0 0; padding: 4px 12px; background-color: #f3f4f6; border: 1px solid #222222; color: #222222; text-decoration: none; border-radius: 0; font-size: 12px; font-family: ${FONT};">
                  ${escapeHtml(tag.name)}
                </a>
                `
                  )
                  .join('')}
              </p>
            </td>
          </tr>
          `
              : ''
          }

`}
    />
  );
}

// Latest uploads from the channel's public RSS feed; the YouTube block falls back to
// copy-only when the feed is unreachable.
async function getLatestYoutubeVideos(limit = 3) {
  try {
    const res = await fetch(YOUTUBE_FEED_URL);
    if (!res.ok) return [];
    const xml = await res.text();
    const decode = (s) =>
      s
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>');
    return [...xml.matchAll(/<entry>([\s\S]*?)<\/entry>/g)]
      .map(([, entry]) => {
        const id = entry.match(/<yt:videoId>([^<]+)<\/yt:videoId>/)?.[1];
        const title = entry.match(/<title>([^<]*)<\/title>/)?.[1];
        const published = entry.match(/<published>([^<]+)<\/published>/)?.[1];
        const link = entry.match(/<link rel="alternate" href="([^"]+)"/)?.[1];
        return id
          ? {
              id,
              title: decode(title || ''),
              published: published || null,
              url: link || `https://www.youtube.com/watch?v=${id}`,
            }
          : null;
      })
      .filter(Boolean)
      .slice(0, limit);
  } catch (error) {
    console.error('[newsletter] YouTube feed fetch failed:', error);
    return [];
  }
}

// Statically generated with a 24h revalidation window so the WordPress/GraphQL
// backend only gets hit once a day, not on every request (this page gets pulled
// heavily around newsletter sends and previously ran getServerSideProps on every hit).
export async function getStaticProps() {
  try {
    // Fetch all data. Posts are sorted newest-first, so 200 comfortably covers a 6-month
    // window for tag tallying while still being cheap to paginate.
    const [postsData, showsData, eventsData, youtubeVideos] = await Promise.all([
      getAllPosts({ first: 200 }),
      getAllShows(),
      getAllEvents(),
      getLatestYoutubeVideos(),
    ]);

    // Process posts
    const posts = Array.isArray(postsData.posts) ? postsData.posts : [];

    // Process shows
    let shows = [];
    if (showsData?.shows?.edges) {
      shows = showsData.shows.edges.map(({ node }) => node);
    } else if (Array.isArray(showsData.shows)) {
      shows = showsData.shows;
    }

    // Split shows into upcoming and past, then sort each group appropriately
    const upcomingShows = shows
      .filter((show) => isUpcoming(show, 'showDate'))
      .sort((a, b) => new Date(a.showDate || a.date) - new Date(b.showDate || b.date)); // ascending: next show first

    const pastShows = shows
      .filter((show) => !isUpcoming(show, 'showDate'))
      .sort((a, b) => new Date(b.showDate || b.date) - new Date(a.showDate || a.date)); // descending: most recent first

    // Process events
    const events = Array.isArray(eventsData.events) ? [...eventsData.events] : [];

    // Sort events by date
    events.sort((a, b) => {
      const dateA = new Date(a.eventDate || a.date);
      const dateB = new Date(b.eventDate || b.date);
      return dateA - dateB; // Ascending for upcoming events
    });

    // Split events into upcoming and past
    const upcomingEvents = events.filter((event) => isUpcoming(event, 'eventDate'));
    const pastEvents = events.filter((event) => !isUpcoming(event, 'eventDate'));

    // Pick a featured sticky event (prefer upcoming, fallback to any sticky)
    const stickyEvents = events.filter((event) => event?.isSticky);
    const featuredStickyEvent = stickyEvents.find((event) => isUpcoming(event, 'eventDate')) || stickyEvents[0] || null;

    // Top 20 tags used on posts/shows/events from the last 6 months
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

    const recentItems = [
      ...posts.filter((post) => new Date(post.date) >= sixMonthsAgo),
      ...shows.filter((show) => new Date(show.showDate || show.date) >= sixMonthsAgo),
      ...events.filter((event) => new Date(event.eventDate || event.date) >= sixMonthsAgo),
    ];

    const tagCounts = new Map();
    recentItems.forEach((item) => {
      getItemTags(item).forEach((tag) => {
        if (!tag?.slug) return;
        const existing = tagCounts.get(tag.slug);
        if (existing) {
          existing.count += 1;
        } else {
          tagCounts.set(tag.slug, { name: tag.name, slug: tag.slug, count: 1 });
        }
      });
    });

    const topTags = Array.from(tagCounts.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, 20);

    return {
      props: {
        posts: posts.slice(0, 5),
        upcomingShows: upcomingShows.slice(0, 1), // Just the next one for the featured section
        pastShows: pastShows.slice(0, 5),
        upcomingEvents,
        pastEvents: pastEvents.slice(0, 5),
        featuredStickyEvent,
        topTags,
        youtubeVideos,
      },
      revalidate: 86400, // 24 hours
    };
  } catch (error) {
    console.error('[newsletter][getStaticProps] Error:', error);
    return {
      props: {
        posts: [],
        upcomingShows: [],
        pastShows: [],
        upcomingEvents: [],
        pastEvents: [],
        featuredStickyEvent: null,
        topTags: [],
        youtubeVideos: [],
      },
      revalidate: 86400, // 24 hours - retry on next request rather than staying broken
    };
  }
}
